import { Box, Button, useToast } from "@chakra-ui/react";
import { ApiMessageStack, Message, TableV2 } from "components";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { AlertStatusEnum } from "enums/alert-status.enum";
import { IntegrationEntitySyncStatusEnum } from "enums/integration-entity-sync-status.enum";
import { keyBy } from "lodash";
import { observer } from "mobx-react";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { TOpenViewSyncNegotiatorExtendedData } from "types/openview.type";
import { IntegrationConfiguratorLayout } from "../../IntegrationConfiguratorLayout";
import { syncOpenViewNegotiatorTableColumnDef } from "./syncOpenViewNegotiatorTableColumnDef";
import { SyncStatusFilter } from "../../SyncStatusFilter";

const DEFAULT_PAGE_SIZE = 10;

interface IProps {
  accountStore: UserAccountStore;
}

export const OpenViewIntegrationUsersConfigurator: FC<IProps> = observer(
  ({ accountStore }) => {
    const toast = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newRelations, setNewRelations] = useState<
      Record<number, TOpenViewSyncNegotiatorExtendedData>
    >({});

    const accountIntegrationsStore = accountStore.accountIntegrationsStore;
    const accountIntegrationsOpenViewStore =
      accountIntegrationsStore.accountIntegrationOpenViewStore;
    const accountIntegrationsOpenViewNegotiatorsStore =
      accountIntegrationsOpenViewStore.accountIntegrationOpenViewNegotiatorsStore;
    const {
      openViewSyncNegotiatorData,
      filteredOpenViewSyncNegotiatorData,
      getSyncStatusFilter,
      setSyncStatusFilter,
    } = accountIntegrationsOpenViewNegotiatorsStore;

    const updateSyncPair = useCallback(
      (syncPair: TOpenViewSyncNegotiatorExtendedData) => {
        setNewRelations({
          ...newRelations,
          [syncPair.userAccountId]: syncPair,
        });
      },
      [setNewRelations, newRelations]
    );

    useEffect(() => {
      const suggestedArray = openViewSyncNegotiatorData.filter(
        syncData =>
          syncData.syncStatus === IntegrationEntitySyncStatusEnum.SUGGESTED_SYNC
      );
      const suggestedMap = keyBy<TOpenViewSyncNegotiatorExtendedData>(
        suggestedArray,
        syncData => syncData.userAccountId
      );
      setNewRelations({
        ...suggestedMap,
      });
    }, [openViewSyncNegotiatorData]);

    const columnsDef = useMemo(() => {
      return syncOpenViewNegotiatorTableColumnDef(accountStore, updateSyncPair);
    }, [accountStore, updateSyncPair]);

    const dataSource = useMemo(() => {
      return filteredOpenViewSyncNegotiatorData.map(syncData => {
        if (!!newRelations[syncData.userAccountId])
          return newRelations[syncData.userAccountId];

        return syncData;
      });
    }, [filteredOpenViewSyncNegotiatorData, newRelations]);

    const submitSyncPairs = useCallback(async () => {
      try {
        setIsSubmitting(true);
        await accountIntegrationsOpenViewNegotiatorsStore.updateAccountOpenViewSyncedNegotiators(
          Object.values(newRelations)
        );
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={
                "iamproperty CRM integration negotiators configuration updated."
              }
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [accountIntegrationsOpenViewNegotiatorsStore, newRelations, toast]);

    const banner = useMemo(() => {
      const notSyncedArray = openViewSyncNegotiatorData.filter(
        syncData =>
          syncData.syncStatus !== IntegrationEntitySyncStatusEnum.SYNCED
      );

      if (notSyncedArray.length > 0) {
        return (
          <Message status={AlertStatusEnum.WARNING}>
            <Box>
              {`${notSyncedArray.length} of your negotiators are not syncing to LeadPro`}
            </Box>
          </Message>
        );
      }

      return null;
    }, [openViewSyncNegotiatorData]);

    const handleSyncFilterChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSyncStatusFilter(e.target.checked);
      },
      [setSyncStatusFilter]
    );

    return (
      <IntegrationConfiguratorLayout>
        <Box fontSize={"xl"}>User mappings</Box>
        {banner}
        <TableV2<TOpenViewSyncNegotiatorExtendedData>
          globalFilterInputPlaceholder={"Search users by name"}
          dataSource={dataSource}
          additionalActions={
            <SyncStatusFilter
              isChecked={getSyncStatusFilter}
              onChange={handleSyncFilterChange}
            />
          }
          columns={columnsDef}
          pageSize={DEFAULT_PAGE_SIZE}
        />
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitSyncPairs}
            isLoading={isSubmitting}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
